import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { useQuery } from "@stadium/services/apollo-client";
import { fetchProductDetail } from "@graphql/queries/stores/products/fetch-detail";

import AdditionalItemsContainer from "@components/store/product-detail/shared/additional-items-container/";
import { useStoreContext } from "@components/store/provider";

import { useSendItemsContext } from "@contexts/send-items-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import { getCountry } from "@store/selectors/country";
import { getMenuId } from "@store/selectors/menu";
import { getIsHideBulkPricing, getStoreCurrencyCode } from "@store/selectors/store";
import { isByoProduct } from "@helpers/products/types";

import styles from "./main.module.scss";

const DefaultAdditionalItemsWidget = ({ productSlug, productData: initialProductData, intent }) => {
  const [skipFirstFetch, setSkipFirstFetch] = useState(true);
  const country = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const { storeNumber, isGlobalCountry } = useStoreContext();
  const menuId = useSelector(getMenuId);
  const { volumeDiscountProductsVariables, isSendToOthers } = useSendItemsContext();
  const { previewMode } = usePreviewModeContext();
  const hideBulkPricing = useSelector(getIsHideBulkPricing);

  const variables = {
    slug: productSlug,
    currency: storeCurrencyCode,
    country: isGlobalCountry ? "" : country.iso,
    ...(isGlobalCountry && { globalZone: isGlobalCountry }),
    storeNumber,
    menuId,
    ...volumeDiscountProductsVariables,
  };

  const { data: queriedData } = useQuery(fetchProductDetail, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: skipFirstFetch || !storeCurrencyCode || !menuId,
  });

  const data = queriedData || initialProductData;

  const product = useMemo(() => data?.product, [data]);

  useEffect(() => {
    if (skipFirstFetch && ((isSendToOthers && hideBulkPricing) || previewMode)) {
      setSkipFirstFetch(false);
    }
  }, [isSendToOthers, storeCurrencyCode]);

  return (
    <div
      className={classNames(styles.fullwidthBg, styles.withRevamp, {
        [styles.byoProductPage]: isByoProduct,
      })}
    >
      <section className={styles.pt2}>
        <article className="t-bg-4">
          <section className={styles.detailWrapper}>
            <AdditionalItemsContainer product={product} intent={intent} />
          </section>
        </article>
      </section>
    </div>
  );
};

export default DefaultAdditionalItemsWidget;
